import { useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ScanLine, Building2, FileSignature, ShoppingCart, Receipt, Monitor, PiggyBank, Calculator, PieChart,
  FileSpreadsheet, Settings, Landmark, Factory, ShieldCheck, Eye, ChevronsLeft, ChevronsRight, Bell, LogOut, Search, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fdatetime, roleLabel, getCompany } from "@/lib/api";

const ALL = ["createur", "comptable"];
export const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: ALL, id: "dashboard", mod: "dashboard" },
  { to: "/factures-a-traiter", label: "Factures à traiter", icon: ScanLine, roles: ["createur"], id: "scans", badge: true, mod: "scans" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Building2, roles: ALL, id: "suppliers", mod: "suppliers" },
  { to: "/societes", label: "Sociétés", icon: Factory, roles: ALL, id: "companies", mod: "companies" },
  { to: "/contrats", label: "Contrats", icon: FileSignature, roles: ALL, id: "contracts", mod: "contracts" },
  { to: "/commandes", label: "Commandes", icon: ShoppingCart, roles: ALL, id: "orders", mod: "orders" },
  { to: "/factures", label: "Factures", icon: Receipt, roles: ALL, id: "invoices", mod: "invoices" },
  { to: "/materiel", label: "Équipements", icon: Monitor, roles: ALL, id: "assets", mod: "assets" },
  { to: "/budgets", label: "Budgets", icon: PiggyBank, roles: ALL, id: "budgets", mod: "budgets" },
  { to: "/comptabilite", label: "Comptabilité", icon: Calculator, roles: ["comptable"], id: "accounting", mod: "accounting" },
  { to: "/immobilisations", label: "Immobilisations", icon: Landmark, roles: ALL, id: "fixed-assets", mod: "fixed-assets" },
  { to: "/kpi", label: "KPI", icon: PieChart, roles: ALL, id: "kpi", mod: "kpi" },
  { to: "/extractions", label: "Extractions", icon: FileSpreadsheet, roles: ALL, id: "extractions", mod: "kpi" },
  { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["comptable"], id: "settings", mod: "settings" },
  { to: "/administration", label: "Administration", icon: ShieldCheck, roles: ["superadmin"], id: "admin" },
];

const SA = (u) => u.role === "superadmin" && !u._readonly;
const visible = (u, n) => {
  if (u.role === "superadmin") return n.id !== "admin" || !u._readonly;
  if (!n.roles.includes(u.role) || (u._modules?.[n.mod] || "active") !== "active") return false;
  return !u.custom_role || (u._perms?.[n.mod] || []).includes("voir");
};
const moduleOfPath = (p) => NAV.filter((n) => n.mod && (n.to === "/" ? p === "/" : p.startsWith(n.to))).sort((a, b) => b.to.length - a.to.length)[0]?.mod;

export const Logo = ({ light }) => (
  <span className="flex items-center gap-2.5" data-testid="app-logo">
    <span className={`text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-[#16163F]"}`}><span className="text-[#A5894E]">OD</span>-Fact</span>
  </span>
);

function CompanySelector() {
  const { user, settings } = useAuth();
  if (!settings) return null;
  const comps = settings.companies.filter((c) => (user._companies || []).includes(c.code));
  const cur = getCompany();
  const change = (v) => { localStorage.setItem("dcf_company", v); window.location.reload(); };
  const groupView = ["comptable", "superadmin"].includes(user.role) && comps.length > 1;
  if (comps.length <= 1 && !groupView) return comps[0] ? <span className="hidden text-sm font-semibold md:block" data-testid="company-current">{comps[0].name}</span> : null;
  return (
    <select className="inp !w-36 shrink-0 text-sm font-semibold sm:!w-56" value={cur || (groupView ? "ALL" : "")} onChange={(e) => change(e.target.value)} data-testid="company-selector">
      {groupView ? <option value="ALL">Tout le groupe</option> : <option value="">Toutes mes sociétés</option>}
      {comps.map((c) => <option key={c.code} value={c.code}>{`${c.name} (${c.code})`}</option>)}
    </select>
  );
}

const LEGACY = null;

function Sidebar({ collapsed, setCollapsed, count, mobileOpen, setMobileOpen }) {
  const { user } = useAuth();
  return (
    <aside className={`fixed inset-y-0 left-0 z-40 flex flex-col bg-[#16163F] grain transition-[width,transform] duration-300 ${collapsed ? "w-[72px]" : "w-64"} ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="sidebar">
      <div className="flex h-16 items-center px-5"><Logo light={true} />{collapsed && <span />}</div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto py-3">
        {NAV.filter((n) => visible(user, n)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)} data-testid={`nav-${n.id}-link`} title={n.label}
            className={({ isActive }) => `relative flex items-center gap-3 border-l-4 px-5 py-2.5 text-sm font-medium transition-colors ${isActive ? "border-[#A5894E] bg-white/5 text-[#A5894E]" : "border-transparent text-white hover:bg-white/5"}`}>
            <n.icon size={18} className="shrink-0" />
            {!collapsed && <span className="flex-1 truncate">{n.label}</span>}
            {!collapsed && user.role === "superadmin" && n.mod && (user._modules?.[n.mod] || "active") !== "active" && <span className="rounded bg-white/10 px-1.5 text-[10px] uppercase text-white/70" data-testid={`nav-${n.id}-state`}>{user._modules[n.mod] === "hidden" ? "masqué" : "désactivé"}</span>}
            {n.badge && count > 0 && <span data-testid="nav-scans-count" className={`rounded-full bg-[#A5894E] px-1.5 text-[11px] font-bold text-[#16163F] ${collapsed ? "absolute right-2 top-1" : ""}`}>{count}</span>}
          </NavLink>
        ))}
      </nav>
      <button className="hidden items-center gap-2 px-5 py-4 text-xs text-white/70 hover:text-white lg:flex" onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle">
        {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /> Replier le menu</>}
      </button>
    </aside>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const nav = useNavigate();
  const t = useRef();
  const go = (v) => {
    setQ(v);
    clearTimeout(t.current);
    if (v.length < 2) return setRes([]);
    t.current = setTimeout(() => api.get("/search", { params: { q: v } }).then((r) => setRes(r.data)), 250);
  };
  return (
    <div className="relative hidden w-full max-w-md sm:block">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B80]" />
      <input className="inp pl-9" placeholder="Recherche globale (factures, scans, fournisseurs, matériel…)" value={q} onChange={(e) => go(e.target.value)}
        onBlur={() => setTimeout(() => setRes([]), 200)} data-testid="global-search-input" />
      {res.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-96 w-full overflow-auto rounded-md border border-[#E4E4EC] bg-white shadow-xl" data-testid="global-search-results">
          {res.map((r, i) => (
            <button key={i} className="flex w-full items-start gap-3 px-3 py-2 text-left text-sm hover:bg-[#A5894E]/10" onMouseDown={() => { nav(r.link); setQ(""); setRes([]); }} data-testid={`global-search-result-${i}`}>
              <span className="mt-0.5 w-24 shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#6B6B80]">{r.type}</span>
              <span className="min-w-0"><span className="block truncate font-semibold">{r.label}</span><span className="block truncate text-xs text-[#6B6B80]">{r.sub}</span></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const toggle = () => { setOpen(!open); if (!open && data.unread) api.post("/notifications/read-all").then(load); };
  return (
    <div className="relative">
      <button className="btn-ghost relative" onClick={toggle} data-testid="notifications-bell">
        <Bell size={18} />
        {data.unread > 0 && <span className="absolute -right-0.5 -top-0.5 rounded-full bg-[#B03A3A] px-1.5 text-[10px] font-bold text-white" data-testid="notifications-unread">{data.unread}</span>}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-[#E4E4EC] bg-white shadow-xl" data-testid="notifications-panel">
          <div className="border-b border-[#E4E4EC] px-4 py-3 text-sm font-bold">Notifications</div>
          <div className="max-h-96 overflow-auto">
            {data.items.length === 0 && <p className="p-6 text-center text-sm text-[#6B6B80]">Aucune notification.</p>}
            {data.items.map((n) => (
              <button key={n.id} className="block w-full border-b border-[#E4E4EC] px-4 py-3 text-left hover:bg-[#A5894E]/10" onClick={() => { setOpen(false); n.link && nav(n.link); }}>
                <span className="block text-sm font-semibold">{n.title}</span>
                <span className="block text-xs text-[#6B6B80]">{n.message}</span>
                <span className="block text-[10px] text-[#6B6B80]">{fdatetime(n.at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ImpersonationBanner() {
  const { user, stopImpersonation } = useAuth();
  if (!user._readonly) return null;
  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-3 bg-[#B03A3A] px-4 py-2 text-sm font-semibold text-white" data-testid="impersonation-banner">
      <Eye size={16} /> Vous consultez OD-Fact en tant que {user.name} ({roleLabel(user)}) — lecture seule
      <button className="rounded border border-white px-3 py-0.5 text-xs font-bold hover:bg-white hover:text-[#B03A3A]" onClick={stopImpersonation} data-testid="impersonation-exit-btn">Quitter</button>
    </div>
  );
}

function ModuleGuard() {
  const { user } = useAuth();
  const loc = useLocation();
  const mod = moduleOfPath(loc.pathname);
  const item = NAV.filter((n) => n.mod && (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to))).sort((a, b) => b.to.length - a.to.length)[0];
  if (user.role !== "superadmin" && item && !item.roles.includes(user.role)) {
    return <div className="card p-10 text-center" data-testid="access-denied-notice"><h2 className="text-lg font-bold">Accès non autorisé</h2><p className="mt-2 text-sm text-[#6B6B80]">Cette page n'est pas disponible pour votre profil.</p></div>;
  }
  if (user.role !== "superadmin" && user.custom_role && mod && !(user._perms?.[mod] || []).includes("voir")) {
    const first = NAV.find((n) => visible(user, n));
    if (first && first.to !== loc.pathname) return <Navigate to={first.to} replace />;
    return <div className="card p-10 text-center" data-testid="access-denied-notice"><h2 className="text-lg font-bold">Accès non autorisé</h2><p className="mt-2 text-sm text-[#6B6B80]">Votre rôle ne donne pas accès à cette page.</p></div>;
  }
  if (user.role !== "superadmin" && mod && user._modules?.[mod] === "disabled") {
    return <div className="card p-10 text-center" data-testid="module-disabled-notice"><h2 className="text-lg font-bold">Module désactivé</h2><p className="mt-2 text-sm text-[#6B6B80]">Ce module a été désactivé par l'administrateur.</p></div>;
  }
  return <Outlet />;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [count, setCount] = useState(0);
  useEffect(() => {
    const load = () => api.get("/scans/count").then((r) => setCount(r.data.count)).catch(() => {});
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, []);
  return (
    <div className="min-h-screen">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} count={count} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <div className={`transition-[padding] duration-300 ${collapsed ? "lg:pl-[72px]" : "lg:pl-64"}`}>
        <ImpersonationBanner />
        <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-[#E4E4EC] bg-white px-4 lg:px-8">
          <button className="btn-ghost lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"><Menu size={20} /></button>
          <span className="hidden sm:block lg:hidden"><Logo /></span>
          <span className="hidden lg:block"><Logo /></span>
          <CompanySelector />
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            {SA(user) && <span className="rounded-full bg-[#A5894E] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#16163F]" data-testid="superadmin-badge">Super admin</span>}
            <Notifications />
            <div className="hidden text-right sm:block">
              <div className="text-sm font-bold" data-testid="current-user-name">{user.name}</div>
              <div className="text-[11px] text-[#6B6B80]" data-testid="current-user-role">{roleLabel(user)}</div>
            </div>
            <button className="btn-ghost" onClick={logout} title="Se déconnecter" data-testid="logout-btn"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="mx-auto max-w-[1500px] p-4 sm:p-6 lg:p-10"><ModuleGuard /></main>
      </div>
    </div>
  );
}
